import {
    Box,
    Collapse,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EEntityType, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import type { IEmployeeCoverage } from '@sharedInterfaces/IOpportunity';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import CertificateCompare from '@src/App/Components/Items/Compare/CertificateCompare/CertificateCompare';
import RoleCompare from '@src/App/Components/Items/Compare/RoleCompare/RoleCompare';
import SkillsLevelCompare from '@src/App/Components/Items/Compare/SkillsLevelCompare/SkillsLevelCompare';
import type { AppState } from '@store/store';
import { ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

interface MvpEmployeeCoverageProps {
    hideAvailability?: boolean;
    hideName?: boolean;
    suggestedEmployee: IEmployeeCoverage;
    inDialog?: boolean;
    expanded?: boolean;
    rightTopButton?: (id: number) => JSX.Element;
    handleDragStart?: (event: React.DragEvent<HTMLDivElement>, item: IEmployeeCoverage | null) => void;
    handleDragEnd?: (event: React.DragEvent<HTMLDivElement>) => void;
}

/**
 * Calculate overall match percentage based on all coverage metrics
 */
function calculateOverallMatch(emp: IEmployeeCoverage): number {
    const skillScore = Math.round(emp.score * 100);
    const skillCoverage = Math.round(emp.skillCoverage * 100);

    const certScore =
        emp.coveredCertificates.length + emp.missingCertificates.length > 0
            ? Math.round(
                  (emp.coveredCertificates.length / (emp.coveredCertificates.length + emp.missingCertificates.length)) *
                      100,
              )
            : 0;

    const roleScore =
        emp.coveredRoles && emp.missingRoles && emp.coveredRoles.length + emp.missingRoles.length > 0
            ? Math.round((emp.coveredRoles.length / (emp.coveredRoles.length + emp.missingRoles.length)) * 100)
            : 0;

    // Weighted average: Skills and coverage are most important
    const weights = { skill: 0.4, coverage: 0.4, cert: 0.15, role: 0.05 };
    return Math.round(
        skillScore * weights.skill +
            skillCoverage * weights.coverage +
            certScore * weights.cert +
            roleScore * weights.role,
    );
}

/**
 * Get color based on percentage value using primary.main with alpha
 */
function getCoverageColor(theme: { palette: { primary: { main: string } } }, percentage: number): string {
    return theme.palette.primary.main;
}

/**
 * Get alpha value based on percentage
 */
function getCoverageAlpha(percentage: number): number {
    if (percentage === 100) return 1.0;
    if (percentage >= 75) return 0.8;
    if (percentage >= 50) return 0.6;
    if (percentage >= 25) return 0.4;
    return 0.2;
}

export function MvpEmployeeCoverage({
    rightTopButton,

    hideName,
    suggestedEmployee,
    inDialog,
    expanded,
    handleDragStart,
    handleDragEnd,
}: MvpEmployeeCoverageProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).sales;
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);

    const [isExpanded, setExpanded] = useState(expanded || false);

    const emp = suggestedEmployee;
    const thisAllEmployee = allEmployees.find((e) => e.id === emp.id);
    const nameSplit = thisAllEmployee ? thisAllEmployee.title.split(' ') : [];
    const firstName = nameSplit.length > 0 ? nameSplit[0] : '';
    const lastName = nameSplit.length > 1 ? nameSplit[nameSplit.length - 1] : '';

    // Calculate metrics
    const overallMatch = calculateOverallMatch(emp);
    const skillScore = Math.round(emp.score * 100);
    const skillCoverage = Math.round(emp.skillCoverage * 100);

    const certScore =
        emp.coveredCertificates.length + emp.missingCertificates.length > 0
            ? Math.round(
                  (emp.coveredCertificates.length / (emp.coveredCertificates.length + emp.missingCertificates.length)) *
                      100,
              )
            : 0;

    const experienceScore = 0; // Placeholder for experience calculation

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                bgcolor: alpha(getCoverageColor(theme, overallMatch), 0.05),
                position: 'relative',
                overflow: 'hidden',
            }}
            draggable={handleDragStart ? true : false}
            onDragStart={(event) => {
                handleDragStart && handleDragStart(event, emp);
            }}
            onDragEnd={handleDragEnd}
        >
            <Box sx={{ p: { lg: 3, md: 2, xs: 2 } }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    {!hideName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                            {thisAllEmployee && (
                                <EmployeePicture
                                    avatarSize={isMobile ? 40 : 45}
                                    openDialogOnClick={true}
                                    employee={{
                                        firstName: firstName,
                                        lastName: lastName,
                                        pictureId: thisAllEmployee.pictureId,
                                        id: thisAllEmployee.id,
                                    }}
                                />
                            )}
                            <Box>
                                <NavLink
                                    to={entityTypeToLink(emp.id, EEntityType.EMPLOYEE)}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Typography
                                        variant={isMobile ? 'subtitle1' : 'h6'}
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.palette.primary.main,
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {emp.title}
                                    </Typography>
                                </NavLink>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(!isExpanded)}
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                },
                            }}
                        >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </IconButton>
                        {rightTopButton && <Box sx={{ ml: 1 }}>{rightTopButton(emp.id)}</Box>}
                    </Box>
                </Box>

                {/* Overall Match */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Gesamt-Match
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 600,
                                    color: alpha(theme.palette.primary.main, getCoverageAlpha(overallMatch)),
                                }}
                            >
                                {overallMatch}%
                            </Typography>
                            <TrendingUp
                                size={20}
                                color={alpha(theme.palette.primary.main, getCoverageAlpha(overallMatch))}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Coverage Bars */}
                <Stack spacing={2}>
                    {/* Skills */}
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                                Skills
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: alpha(theme.palette.primary.main, getCoverageAlpha(skillScore)) }}
                            >
                                {skillScore}%
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={skillScore}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: alpha(theme.palette.primary.main, getCoverageAlpha(skillScore)),
                                    borderRadius: 4,
                                },
                            }}
                        />
                    </Box>

                    {/* Kompetenzen (Skill Coverage) */}
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                                Kompetenzen
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: alpha(theme.palette.primary.main, getCoverageAlpha(skillCoverage)) }}
                            >
                                {skillCoverage}%
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={skillCoverage}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: alpha(theme.palette.primary.main, getCoverageAlpha(skillCoverage)),
                                    borderRadius: 4,
                                },
                            }}
                        />
                    </Box>

                    {/* Zertifikate */}
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                                Zertifikate
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: alpha(theme.palette.primary.main, getCoverageAlpha(certScore)) }}
                            >
                                {certScore}%
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={certScore}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: alpha(theme.palette.primary.main, getCoverageAlpha(certScore)),
                                    borderRadius: 4,
                                },
                            }}
                        />
                    </Box>

                    {/* Erfahrung */}
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                                Erfahrung
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: alpha(theme.palette.primary.main, getCoverageAlpha(experienceScore)) }}
                            >
                                {experienceScore}%
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={experienceScore}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: alpha(theme.palette.primary.main, getCoverageAlpha(experienceScore)),
                                    borderRadius: 4,
                                },
                            }}
                        />
                    </Box>
                </Stack>

                {/* Expandable Content */}
                <Collapse in={isExpanded}>
                    <Stack spacing={2} sx={{ mt: 3 }}>
                        {emp.coveredRoles && emp.coveredRoles.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}
                                >
                                    {langStrings.coveredRoles}
                                </Typography>
                                <RoleCompare size="medium" inDialog={inDialog || undefined} roles={emp.coveredRoles} />
                            </Box>
                        )}
                        {emp.missingRoles && emp.missingRoles.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}
                                >
                                    {langStrings.missingRoles}
                                </Typography>
                                <RoleCompare
                                    size="medium"
                                    negative={true}
                                    inDialog={inDialog || undefined}
                                    roles={emp.missingRoles}
                                />
                            </Box>
                        )}
                        {emp.coveredSkills.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}
                                >
                                    {langStrings.coveredSkills}
                                </Typography>
                                <SkillsLevelCompare
                                    employeeId={thisAllEmployee?.id}
                                    size="medium"
                                    inDialog={inDialog || undefined}
                                    skills={emp.coveredSkills}
                                    levelBadgeVariant="arrow"
                                    skillContext="covered"
                                />
                            </Box>
                        )}
                        {emp.missingSkills.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}
                                >
                                    {langStrings.missingSkills}
                                </Typography>
                                <SkillsLevelCompare
                                    employeeId={thisAllEmployee?.id}
                                    size="medium"
                                    inDialog={inDialog || undefined}
                                    skills={emp.missingSkills}
                                    levelBadgeVariant="arrow"
                                    skillContext="missing"
                                />
                            </Box>
                        )}
                        {emp.coveredCertificates.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.success.main }}
                                >
                                    {langStrings.coveredCertificates}
                                </Typography>
                                <CertificateCompare
                                    size="medium"
                                    inDialog={inDialog || undefined}
                                    certificates={emp.coveredCertificates}
                                />
                            </Box>
                        )}
                        {emp.missingCertificates.length > 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 1, color: theme.palette.error.main }}
                                >
                                    {langStrings.missingCertificates}
                                </Typography>
                                <CertificateCompare
                                    size="medium"
                                    negative={true}
                                    inDialog={inDialog || undefined}
                                    certificates={emp.missingCertificates}
                                />
                            </Box>
                        )}
                    </Stack>
                </Collapse>
            </Box>
        </Paper>
    );
}

export default MvpEmployeeCoverage;
